import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  TrendingUp,
  MessageSquare,
  Plus,
  Target,
  Users,
  Calendar,
  BarChart3,
  Activity,
  Award
} from 'lucide-react';
import api from '../services/api';
import { Task, Project } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import '../index.css';

interface Stats {
  total_tasks: number;
  completed_tasks: number;
  in_progress_tasks: number;
  todo_tasks: number;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { getThemeColors } = useTheme();
  const colors = getThemeColors();
  
  const [stats, setStats] = useState<Stats>({
    total_tasks: 0,
    completed_tasks: 0,
    in_progress_tasks: 0,
    todo_tasks: 0
  });
  const [recentTasks, setRecentTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [aiCommand, setAiCommand] = useState('');
  const [aiResponse, setAiResponse] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [tasksRes, projectsRes] = await Promise.all([
        api.get('/api/tasks'),
        api.get('/api/projects')
      ]);

      const tasks = tasksRes.data;
      const projects = projectsRes.data;

      // Calculate stats
      const stats = {
        total_tasks: tasks.length,
        completed_tasks: tasks.filter((t: Task) => t.status === 'completed').length,
        in_progress_tasks: tasks.filter((t: Task) => t.status === 'in-progress').length,
        todo_tasks: tasks.filter((t: Task) => t.status === 'todo').length
      };

      setStats(stats);
      setRecentTasks(tasks.slice(0, 5));
      setProjects(projects.slice(0, 3));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const handleAiCommand = async () => {
    if (!aiCommand.trim()) return;

    try {
      const response = await api.post('/api/ai/command', {
        command: aiCommand
      });
      setAiResponse(response.data.message);
      setAiCommand('');
      fetchDashboardData(); // Refresh data after AI command
    } catch (error) {
      console.error('Error executing AI command:', error);
      setAiResponse('Error executing command');
    }
  };

  const completionRate = stats.total_tasks > 0 
    ? Math.round((stats.completed_tasks / stats.total_tasks) * 100) 
    : 0;

  return (
    <div className={`min-h-screen bg-gradient-to-br ${colors.background}`}>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className={`text-3xl font-bold bg-gradient-to-r ${colors.primary} bg-clip-text text-transparent mb-2`}>
                Dashboard
              </h1>
              <p className={colors.textSecondary}>
                Welcome back! Here's what's happening with your projects.
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button 
                onClick={() => navigate('/projects?create=true')}
                className={`px-4 py-2 bg-gradient-to-r ${colors.gradient} hover:${colors.gradientHover} text-white rounded-lg transition-all duration-200 flex items-center space-x-2 font-medium`}
              >
                <Plus className="w-4 h-4" />
                <span>New Project</span>
              </button>
              <button 
                onClick={() => navigate('/tasks?create=true')}
                className="px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg transition-all duration-200 flex items-center space-x-2 font-medium"
              >
                <Plus className="w-4 h-4" />
                <span>New Task</span>
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className={`${colors.surface} backdrop-blur-xl rounded-xl p-6 border ${colors.border} shadow-lg`}>
              <div className="flex items-center">
                <div className={`p-3 bg-gradient-to-r ${colors.primary} rounded-lg`}>
                  <Target className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className={`text-sm font-medium ${colors.textSecondary}`}>Total Tasks</p>
                  <p className={`text-2xl font-bold ${colors.text}`}>{stats.total_tasks}</p>
                </div>
              </div>
            </div>

            <div className={`${colors.surface} backdrop-blur-xl rounded-xl p-6 border ${colors.border} shadow-lg`}>
              <div className="flex items-center">
                <div className="p-3 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className={`text-sm font-medium ${colors.textSecondary}`}>Completed</p>
                  <p className={`text-2xl font-bold ${colors.text}`}>{stats.completed_tasks}</p>
                </div>
              </div>
            </div>

            <div className={`${colors.surface} backdrop-blur-xl rounded-xl p-6 border ${colors.border} shadow-lg`}>
              <div className="flex items-center">
                <div className="p-3 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className={`text-sm font-medium ${colors.textSecondary}`}>In Progress</p>
                  <p className={`text-2xl font-bold ${colors.text}`}>{stats.in_progress_tasks}</p>
                </div>
              </div>
            </div>

            <div className={`${colors.surface} backdrop-blur-xl rounded-xl p-6 border ${colors.border} shadow-lg`}>
              <div className="flex items-center">
                <div className="p-3 bg-gradient-to-r from-slate-500 to-gray-600 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className={`text-sm font-medium ${colors.textSecondary}`}>To Do</p>
                  <p className={`text-2xl font-bold ${colors.text}`}>{stats.todo_tasks}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Overview */}
          <div className={`${colors.surface} backdrop-blur-xl rounded-xl p-6 border ${colors.border} shadow-lg`}>
            <h3 className={`text-lg font-semibold ${colors.text} mb-4 flex items-center`}>
              <BarChart3 className="w-5 h-5 mr-2" />
              Progress Overview
            </h3>
            <div className="space-y-4">
              <div>
                <div className={`flex justify-between text-sm ${colors.textSecondary} mb-2`}>
                  <span>Overall Completion</span>
                  <span>{completionRate}%</span>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3">
                  <div 
                    className={`bg-gradient-to-r ${colors.primary} h-3 rounded-full transition-all duration-300`}
                    style={{ width: `${completionRate}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* AI Assistant */}
          <div className={`${colors.surface} backdrop-blur-xl rounded-xl p-6 border ${colors.border} shadow-lg`}>
            <h3 className={`text-lg font-semibold ${colors.text} mb-4 flex items-center`}>
              <MessageSquare className="w-5 h-5 mr-2" />
              AI Assistant
            </h3>
            <div className="space-y-4">
              <div className="flex space-x-3">
                <input
                  type="text"
                  value={aiCommand}
                  onChange={(e) => setAiCommand(e.target.value)}
                  placeholder="Try: 'move my 9am task to complete' or 'delete all completed tasks'"
                  className={`flex-1 p-3 bg-white dark:bg-slate-700 border ${colors.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${colors.text}`}
                  onKeyPress={(e) => e.key === 'Enter' && handleAiCommand()}
                />
                <button 
                  onClick={handleAiCommand} 
                  className={`px-6 py-3 bg-gradient-to-r ${colors.gradient} hover:${colors.gradientHover} text-white rounded-lg transition-all duration-200 font-medium`}
                >
                  Send
                </button>
              </div>
              {aiResponse && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-blue-800 dark:text-blue-200">{aiResponse}</p>
                </div>
              )}
            </div>
          </div>

          {/* Recent Tasks & Projects */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Tasks */}
            <div className={`${colors.surface} backdrop-blur-xl rounded-xl p-6 border ${colors.border} shadow-lg`}>
              <div className="flex justify-between items-center mb-4">
                <h3 className={`text-lg font-semibold ${colors.text} flex items-center`}>
                  <Activity className="w-5 h-5 mr-2" />
                  Recent Tasks
                </h3>
                <button 
                  onClick={() => navigate('/tasks')}
                  className="px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg transition-all duration-200 text-sm font-medium"
                >
                  View All
                </button>
              </div>
              <div className="space-y-3">
                {recentTasks.length > 0 ? (
                  recentTasks.map((task) => (
                    <div key={task.id} className={`flex items-center justify-between p-3 bg-white dark:bg-slate-700 rounded-lg border ${colors.border}`}>
                      <div>
                        <p className={`font-medium ${colors.text}`}>{task.title}</p>
                        <p className={`text-sm ${colors.textSecondary}`}>{task.project_name}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        task.status === 'completed' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                          : task.status === 'in-progress'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200'
                          : 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-200'
                      }`}>
                        {task.status}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className={`text-center py-8 ${colors.textSecondary}`}>
                    <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No tasks yet</p>
                    <button 
                      onClick={() => navigate('/tasks?create=true')}
                      className="mt-2 px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg transition-all duration-200 text-sm"
                    >
                      Create your first task
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Active Projects */}
            <div className={`${colors.surface} backdrop-blur-xl rounded-xl p-6 border ${colors.border} shadow-lg`}>
              <div className="flex justify-between items-center mb-4">
                <h3 className={`text-lg font-semibold ${colors.text} flex items-center`}>
                  <Award className="w-5 h-5 mr-2" />
                  Active Projects
                </h3>
                <button 
                  onClick={() => navigate('/projects')}
                  className="px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg transition-all duration-200 text-sm font-medium"
                >
                  View All
                </button>
              </div>
              <div className="space-y-3">
                {projects.length > 0 ? (
                  projects.map((project) => (
                    <div key={project.id} className={`p-3 bg-white dark:bg-slate-700 rounded-lg border ${colors.border}`}>
                      <p className={`font-medium ${colors.text}`}>{project.name}</p>
                      <div className="flex justify-between text-sm mt-1">
                        <span className={colors.textSecondary}>
                          {project.completed_tasks}/{project.task_count} tasks completed
                        </span>
                        <span className={colors.textSecondary}>
                          {project.task_count > 0 ? Math.round((project.completed_tasks / project.task_count) * 100) : 0}%
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 mt-2">
                        <div 
                          className={`bg-gradient-to-r ${colors.primary} h-2 rounded-full transition-all duration-300`}
                          style={{ width: `${project.task_count > 0 ? (project.completed_tasks / project.task_count) * 100 : 0}%` }}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={`text-center py-8 ${colors.textSecondary}`}>
                    <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No projects yet</p>
                    <button 
                      onClick={() => navigate('/projects?create=true')}
                      className="mt-2 px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg transition-all duration-200 text-sm"
                    >
                      Create your first project
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 